/* eslint-disable @typescript-eslint/no-unsafe-function-type */
export type Tags = Record<string, unknown>;

export type Trace = {
  traceId: string;
  sessionId: string;
  reqId?: string;
  doSave: boolean;
  tags: Tags;
};

export type Span = {
  spanId: string;
  traceId: string;
  finish: (error?: Error) => void;
  getTags: () => Tags;
  setTags: (tags: Tags) => void;
};

// ASYNC_FN_MATCHER matches any stringified function that accepts one or more arguments/callbacks
// YES: (arg) => { ... }
// YES: arg => { ... }
// YES: function hello ( goodbye ) { ... }
// NO: ( ) => { ... }
// NO: function test() { ... }
// NO: function( ) { ... }
const ASYNC_FN_MATCHER = /^(\(?\s*\w+\s*\)?\s*=>|function(\s+\w*\s*)?\(\s*\w)/;

export abstract class Tracer {
  public static isAsyncFunction(fn: Function) {
    return ASYNC_FN_MATCHER.test(fn.toString());
  }

  public abstract trace<T>(fn: (...args: Array<unknown>) => T, name: string, tags?: Tags): T;
  public abstract getCurrentTrace(): Trace;
  public abstract makeTraceID(): string;
  protected abstract getCurrentSpan(): Span | undefined;

  public wrap<T extends Function>(fn: T, name = fn.name, tags?: Tags) {
    return ((...args: Array<unknown>) => {
      return this.trace(() => fn.apply(this, args), name, tags);
    }) as unknown as T;
  }

  protected getCurrentTags(): Tags {
    const trace = this.getCurrentTrace();
    const span = this.getCurrentSpan();
    return {
      ...trace.tags,
      ...span?.getTags(),
    };
  }

  protected setSaveTrace(doSave: boolean): void {
    const trace = this.getCurrentTrace();
    trace.doSave = doSave;
  }
}
